import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, ArrowLeft, Tag } from 'lucide-react';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadPost();
  }, [slug]);

  const loadPost = async () => {
    setLoading(true);
    setError(false);
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
    } catch (err) {
      console.error('Erreur chargement article:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatBlogContent = (content) => {
    if (!content) return '';
    
    let formatted = content
      // Gras : **texte** → <strong>texte</strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Italique : *texte* → <em>texte</em>
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Titres : ## Titre → <h2>Titre</h2>
      .replace(/^## (.*?)$/gm, '<h2>$1</h2>')
      .replace(/^### (.*?)$/gm, '<h3>$1</h3>')
      // Listes : - item ou • item
      .replace(/^[•\-] (.*?)$/gm, '<li>$1</li>')
      // Images : [Image: URL]
      .replace(/\[Image:\s*(.*?)\]/g, '<img src="$1" alt="Article" style="width: 100%; max-width: 800px; height: auto; margin: 24px 0; border-radius: 8px;" />')
      // Vidéos YouTube : [Video: URL]
      .replace(/\[Video:\s*(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+).*?\]/g, 
        '<div style="position: relative; padding-bottom: 56.25%; height: 0; margin: 24px 0;"><iframe src="https://www.youtube.com/embed/$1" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0; border-radius: 8px;" allowfullscreen></iframe></div>')
      // Liens : [Texte](URL)
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: var(--gold-primary); text-decoration: underline;">$1</a>')
      // Paragraphes
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br/>');

    // Envelopper dans <p> si nécessaire
    if (!formatted.startsWith('<')) {
      formatted = '<p>' + formatted + '</p>';
    }

    // Envelopper les listes
    formatted = formatted.replace(/(<li>.*?<\/li>)/gs, '<ul>$1</ul>');

    return formatted;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
          Chargement de l'article...
        </p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
        <h1 className="hero-medium" style={{ marginBottom: '16px' }}>
          Article introuvable
        </h1>
        <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
          Désolé, cet article n'existe pas ou a été supprimé.
        </p>
        <Link
          to="/blog"
          className="btn-primary"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            textDecoration: 'none'
          }}
        >
          <ArrowLeft size={18} />
          Retour au blog
        </Link>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-secondary)' }}>
      {post && (
        <SEO 
          title={`${post.title} - Nina Care Institut`}
          description={post.excerpt}
          keywords={`${post.category}, beauté, soins, Nina Care`}
          image={post.featured_image}
          url={`${BACKEND_URL}/blog/${post.slug}`}
          type="article"
          publishedTime={post.published_at || post.created_at}
          modifiedTime={post.updated_at}
          author={post.author}
          category={post.category}
        />
      )}
      
      {/* Image de couverture */}
      {post.featured_image && (
        <div style={{
          width: '100%',
          height: '400px',
          overflow: 'hidden',
          background: `url(${post.featured_image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
            display: 'flex',
            alignItems: 'flex-end',
            padding: '40px 20px'
          }}>
            <div className="container" style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
              <Link
                to="/blog"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: 'white',
                  textDecoration: 'none',
                  marginBottom: '16px',
                  opacity: 0.9
                }}
              >
                <ArrowLeft size={18} />
                Retour au blog
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Contenu de l'article */}
      <article style={{ padding: '60px 20px' }}>
        <div className="container" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {!post.featured_image && (
            <Link
              to="/blog"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: 'var(--gold-primary)',
                textDecoration: 'none',
                marginBottom: '24px'
              }}
            >
              <ArrowLeft size={18} />
              Retour au blog
            </Link>
          )}

          {/* Catégorie */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Tag size={16} style={{ color: 'var(--gold-primary)' }} />
            <span className="body-regular" style={{ color: 'var(--gold-primary)', fontWeight: 600 }}>
              {post.category}
            </span>
          </div>

          {/* Titre */}
          <h1 className="hero-large" style={{ marginBottom: '24px', lineHeight: '1.2' }}>
            {post.title}
          </h1>

          {/* Métadonnées */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            marginBottom: '40px',
            paddingBottom: '24px',
            borderBottom: '2px solid var(--border-light)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={18} style={{ color: 'var(--text-secondary)' }} />
              <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {formatDate(post.published_at || post.created_at)}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={18} style={{ color: 'var(--text-secondary)' }} />
              <span className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                {post.author}
              </span>
            </div>
          </div>

          {/* Contenu */}
          <div
            className="blog-content"
            style={{
              fontSize: '17px',
              lineHeight: '1.8',
              color: 'var(--text-primary)'
            }}
            dangerouslySetInnerHTML={{ __html: formatBlogContent(post.content) }}
          />

          {/* Call to action */}
          <div style={{
            marginTop: '60px',
            padding: '40px',
            background: 'linear-gradient(135deg, rgba(201, 169, 97, 0.1) 0%, rgba(201, 169, 97, 0.05) 100%)',
            borderRadius: '8px',
            textAlign: 'center',
            border: '1px solid var(--border-light)'
          }}>
            <h3 className="heading-2" style={{ marginBottom: '16px' }}>
              Envie de découvrir nos soins ?
            </h3>
            <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '24px' }}>
              Prenez rendez-vous dès maintenant et profitez de notre expertise en beauté naturelle
            </p>
            <Link
              to="/contact"
              className="btn-primary"
              style={{ textDecoration: 'none' }}
            >
              Prendre rendez-vous
            </Link>
          </div>
        </div>
      </article>

      <style>
        {`
          .blog-content h2 {
            font-size: 28px;
            font-weight: 700;
            margin: 32px 0 16px;
            color: var(--text-primary);
          }
          .blog-content h3 {
            font-size: 22px;
            font-weight: 600;
            margin: 24px 0 12px;
            color: var(--text-primary);
          }
          .blog-content p {
            margin: 16px 0;
          }
          .blog-content ul, .blog-content ol {
            margin: 16px 0;
            padding-left: 24px;
          }
          .blog-content li {
            margin: 8px 0;
          }
          .blog-content strong {
            font-weight: 600;
            color: var(--gold-primary);
          }
          .blog-content em {
            font-style: italic;
          }
        `}
      </style>
    </div>
  );
};

export default BlogPost;
